import { loginSchema, type LoginDTO } from '@form-factory/schema/contracts';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { TRPCClientError } from '@trpc/client';
import { Button, Separator, toast } from '@utima/ui';
import type * as Form from '@utima/ui-informed';
import { useState } from 'react';

import { BasicForm } from '@/components/form/BasicForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useUser } from '@/hooks';
import { trpc, trpcClient } from '@/services/trpc';

export const Route = createFileRoute('/_authLayout/login/')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    return {
      redirect: search.redirect as string,
    };
  },
});

const initialValues: LoginDTO = {
  email: '',
  password: '',
};

export function LoginPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { setJwt, setUser, setCurrentOrganizationId, logout } = useUser();

  const loginMutation = trpc.auth.login.useMutation();
  const search = Route.useSearch();
  const [isLoading, setIsLoading] = useState(false);

  // Normal redirect flow after login
  const handleRedirect = async () => {
    if (search.redirect) {
      navigate({ to: search.redirect, replace: true });
    } else {
      navigate({ to: '/' });
    }
  };

  const onSubmit = (formState: Form.TypedFormState<LoginDTO>) => {
    setIsLoading(true);

    return loginMutation.mutate(formState.values, {
      onError: (error: unknown) => {
        logout();

        try {
          if (error instanceof TRPCClientError) {
            const { data, message } = error;

            if (data.code === 'UNAUTHORIZED') {
              if (message.includes('Account requires verification')) {
                toast.error(t`Account not verified`, {
                  description: t`Account is not verified. Check your email for verification link.`,
                });
              } else {
                toast.error(t`An error occurred`, {
                  description: t`Invalid credentials.`,
                });
              }
            }

            if (data.code === 'NOT_FOUND') {
              toast.error(t`User not found`, {
                description: t`User with this email does not exist.`,
              });
            }
          } else {
            toast.error(t`An error occurred`, {
              description: t`Could not log in, please try again.`,
            });
          }
        } catch {
          setIsLoading(false);
          toast.error(t`An error occurred`, {
            description: t`Could not log in, please try again.`,
          });
        }
      },
      onSuccess: async data => {
        setJwt(data.access);

        // Fetch user me
        const user = await trpcClient.users.me.query();
        setUser(user);

        const organization = await trpcClient.organizations.findOne.query({
          id: data.user.currentOrganizationId,
        });

        if (!organization) {
          toast.error(t`Organization not found`, {
            description: t`Organization not found.`,
          });

          return handleRedirect();
        }
        setCurrentOrganizationId(organization?.id);

        /**
         * Handle first login flow, by redirecting to the documents modal
         * if the user is logging in for the first time and updating the flag
         * via API.
         */
        if (user.firstLogin) {
          try {
            // Update the flag via API
            const [chats] = await Promise.all([
              trpcClient.chats.findAll.query({
                where: {
                  organization: data.user.currentOrganizationId,
                },
                pagination: {
                  pageSize: 1,
                },
              }),
              trpcClient.users.update.mutate({
                id: user.id,
                firstLogin: false,
              }),
            ]);

            const [chat] = chats.items;

            if (!chat?.id) {
              return handleRedirect();
            }

            // Redirect to documents modal
            navigate({
              to: '/chats/$chatId/documents',
              params: { chatId: chat.id },
              search: {
                open: organization.url,
              },
            });
          } catch (error) {
            console.error('Failed to fetch chats:', error);

            return handleRedirect();
          }
        } else {
          return handleRedirect();
        }
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const { schema } = useFieldsSchema(
    [
      {
        name: 'email',
        label: t`Email`,
        required: true,
        uiProps: {
          type: 'email',
          'data-cta': 'admin-login-email-input',
        },
      },
      {
        name: 'password',
        label: t`Password`,
        required: true,
        uiProps: {
          type: 'password',
          passwordPreview: true,
          'data-cta': 'admin-login-password-input',
        },
      },
    ],
    [t],
  );

  return (
    <BasicForm
      onSubmit={onSubmit}
      initialValues={initialValues}
      zodSchema={loginSchema}
      className='flex w-full flex-col gap-6'
    >
      <h1 className='text-center text-2xl font-bold'>{t`Login`}</h1>

      <SchemaFields schema={schema} />

      <Button
        data-cta='admin-login-submit-button'
        variant='primary'
        size='lg'
        type='submit'
        disabled={isLoading}
        loading={isLoading}
      >
        {t`Login`}
      </Button>

      <div className='flex flex-row items-center justify-center gap-3'>
        <Link
          data-cta='admin-login-forgotten-password-link'
          to='/forgotten-password'
          className='text-sm hover:text-primary hover:underline'
        >
          {t`Forgot password?`}
        </Link>
        <Separator orientation='vertical' className='h-4' />
        <Link
          to='/register'
          data-cta='admin-login-register-link'
          className='text-sm font-semibold hover:text-primary hover:underline'
        >
          {t`Register`}
        </Link>
      </div>
    </BasicForm>
  );
}
